'use client'

import { Fragment } from 'react'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import { Check, Flag, Lock, Mail, Phone, Send, UserRound, X } from 'lucide-react'
import { useVendorReport } from '@/lib/commerce/hooks'
import { REPORT_REASONS, REPORT_MESSAGE_MAX, type ReportReasonTone } from '@/utils/report'

const tones: Record<ReportReasonTone, { ic: string, bg: string, border: string, on: string }> = {
  red: { ic: '#fda4af', bg: 'rgba(244,63,94,0.16)', border: 'rgba(244,63,94,0.3)', on: 'rgba(244,63,94,0.5)' },
  amber: { ic: '#fcd34d', bg: 'rgba(245,158,11,0.16)', border: 'rgba(245,158,11,0.3)', on: 'rgba(245,158,11,0.5)' },
  slate: { ic: '#cbd5e1', bg: 'rgba(148,163,184,0.16)', border: 'rgba(148,163,184,0.3)', on: 'rgba(148,163,184,0.5)' },
}

const inputStyle: React.CSSProperties = {
  background: 'rgba(0,0,0,0.3)',
  border: '1px solid rgba(255,255,255,0.13)',
  color: 'white',
}

interface ReportVendorDialogProps {
  open: boolean
  onClose: () => void
  vendorId: string
  vendorName?: string
}

export default function ReportVendorDialog({ open, onClose, vendorId, vendorName }: ReportVendorDialogProps) {
  const r = useVendorReport(vendorId, onClose)

  const handleClose = () => {
    if (r.submitting) return
    onClose()
    r.reset()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await r.submit()
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <TransitionChild as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-[2px]" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4">
            <TransitionChild as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 translate-y-2 sm:scale-[0.985]" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-2 sm:scale-[0.985]">
              <DialogPanel
                className="w-full max-w-[560px] max-h-[90vh] sm:max-h-[85vh] rounded-t-2xl sm:rounded-3xl overflow-hidden flex flex-col"
                style={{
                  background: 'linear-gradient(180deg, #16161c 0%, #0c0c10 100%)',
                  border: '1px solid rgba(255,255,255,0.13)',
                  boxShadow: '0 40px 100px -20px rgba(0,0,0,0.75)',
                }}
              >
                <div className="relative px-6 pt-6 pb-5 shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      <div
                        className="h-10 w-10 rounded-xl grid place-items-center shrink-0"
                        style={{ background: 'rgba(244,63,94,0.16)', border: '1px solid rgba(244,63,94,0.3)', color: '#fda4af' }}
                      >
                        <Flag className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[16px] font-semibold leading-tight tracking-[-0.005em] text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>
                          Дэлгүүрийг мэдэгдэх
                        </div>
                        <div className="mt-1 text-[12.5px] leading-snug" style={{ color: 'rgba(255,255,255,0.55)' }}>
                          {vendorName ? <><span className="font-semibold text-white">{vendorName}</span> дэлгүүрийн талаар мэдээлэл өгнө үү.</> : 'Дэлгүүрийн талаар мэдээлэл өгнө үү. Shop.mn багийнхан хянана.'}
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="h-8 w-8 rounded-lg grid place-items-center transition-colors cursor-pointer shrink-0"
                      style={{ color: 'rgba(255,255,255,0.5)' }}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="px-6 py-5 overflow-y-auto pb-[max(1.25rem,env(safe-area-inset-bottom))] scrollbar-dark">
                  <div className="mb-5">
                    <div className="flex items-center gap-1.5 mb-2">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: 'rgba(255,255,255,0.7)' }}>
                        Шалтгаан <span style={{ color: '#fda4af' }}>*</span>
                      </div>
                      <span className="text-[11px] font-normal ml-auto" style={{ color: 'rgba(255,255,255,0.4)' }}>
                        {r.reason ? '1 сонгосон' : 'Аль нэгийг сонго'}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {REPORT_REASONS.map(item => {
                        const active = r.reason === item.value
                        const t = tones[item.tone]
                        return (
                          <button
                            type="button"
                            key={item.value}
                            onClick={() => r.setReason(item.value)}
                            className="w-full text-left rounded-xl px-3.5 py-3 transition-all flex items-start gap-3 cursor-pointer"
                            style={active
                              ? { background: 'rgba(255,255,255,0.06)', border: `1px solid ${t.on}` }
                              : { background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)' }}
                          >
                            <div
                              className="h-9 w-9 rounded-lg grid place-items-center shrink-0"
                              style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.ic }}
                            >
                              <item.Icon className="h-4.5 w-4.5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-[13.5px] font-semibold leading-tight text-white">{item.label}</div>
                              <div className="text-[12px] leading-snug mt-0.5" style={{ color: 'rgba(255,255,255,0.55)' }}>{item.sub}</div>
                            </div>
                            <div
                              className="h-5 w-5 rounded-full grid place-items-center shrink-0 mt-0.5"
                              style={active
                                ? { background: 'white', border: '2px solid white' }
                                : { background: 'transparent', border: '2px solid rgba(255,255,255,0.2)' }}
                            >
                              {active && <Check className="h-3 w-3" style={{ color: '#0a0a0c' }} strokeWidth={3.5} />}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] mb-2" style={{ color: 'rgba(255,255,255,0.7)' }}>
                      Юу болсныг тайлбарлана уу <span style={{ color: '#fda4af' }}>*</span>
                    </div>
                    <textarea
                      value={r.message}
                      onChange={(e: any) => r.setMessage(e.target.value.slice(0, REPORT_MESSAGE_MAX))}
                      rows={4}
                      placeholder="Жишээ: Бараагаа захиалаад төлбөрөө шилжүүлсэн боловч хариу ирэхгүй 5 хоног боллоо. Утсаар холбогдохгүй..."
                      className="w-full rounded-xl px-4 py-3 text-[13.5px] outline-none focus:ring-0 resize-none leading-6 placeholder:text-white/30 scrollbar-dark"
                      style={inputStyle}
                    />
                    <div className="mt-1.5 flex items-center justify-between text-[11px]" style={{ color: 'rgba(255,255,255,0.4)' }}>
                      <span>Захиалгын дугаар, огноо, мөнгөн дүн зэрэг тодорхой нотолгоо хавсаргахыг зөвлөе.</span>
                      <span className="tabular-nums shrink-0 ml-3">{r.message.length}/{REPORT_MESSAGE_MAX}</span>
                    </div>
                  </div>

                  <div className="mb-4 rounded-xl overflow-hidden" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.1)' }}>
                    <div className="flex items-center gap-2.5 px-3.5 py-3">
                      <UserRound className="h-4 w-4" style={{ color: 'rgba(255,255,255,0.5)' }} />
                      <span className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: 'rgba(255,255,255,0.7)' }}>
                        Холбоо барих мэдээлэл <span style={{ color: '#fda4af' }}>*</span>
                      </span>
                      <span
                        className="text-[10.5px] font-medium tracking-[0.05em] uppercase px-2 py-0.5 rounded-full"
                        style={{ background: 'rgba(244,63,94,0.16)', color: '#fda4af' }}
                      >
                        Заавал
                      </span>
                    </div>
                    <div className="px-3.5 pb-3.5 pt-1 space-y-3">
                      <div className="text-[12px] leading-snug" style={{ color: 'rgba(255,255,255,0.5)' }}>
                        Мэдэгдлийг шалгахад бид тантай холбогдох тул мэдээллээ бүрэн бөглөнө үү.
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        <DialogField label="Нэр" icon={UserRound} value={r.guestName} onChange={r.setGuestName} placeholder="Хэрэглэгч" />
                        <DialogField label="Утас" icon={Phone} value={r.guestPhone} onChange={r.setGuestPhone} placeholder="80000000" />
                      </div>
                      <DialogField label="Имэйл" icon={Mail} type="email" value={r.guestEmail} onChange={r.setGuestEmail} placeholder="user@shop.mn" />
                    </div>
                  </div>

                  <div className="flex items-start gap-2 text-[11.5px] leading-snug" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    <Lock className="h-3.5 w-3.5 mt-px shrink-0" />
                    <span>Мэдэгдэлд явуулсан холбоо барих мэдээллийг нууцлах ба зөвхөн Shop.mn-д мэдэгдэх болно.</span>
                  </div>

                  <div className="mt-5 flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={handleClose}
                      disabled={r.submitting}
                      className="h-11 px-5 rounded-full text-[13px] font-medium transition-colors cursor-pointer disabled:opacity-50"
                      style={{ background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}
                    >
                      Болих
                    </button>
                    <button
                      type="submit"
                      disabled={r.submitting}
                      className="h-11 px-5 rounded-full text-[13px] font-semibold inline-flex items-center gap-2 transition-opacity cursor-pointer hover:opacity-90 disabled:opacity-60"
                      style={{ background: '#dc2626', color: 'white' }}
                    >
                      <Send className="h-3.5 w-3.5" />
                      {r.submitting ? 'Илгээж байна...' : 'Мэдэгдэл илгээх'}
                    </button>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

function DialogField({ label, icon: Icon, value, onChange, placeholder, type = 'text' }: { label: string, icon: any, value: string, onChange: (v: string) => void, placeholder?: string, type?: string }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] mb-1.5" style={{ color: 'rgba(255,255,255,0.7)' }}>{label} <span style={{ color: '#fda4af' }}>*</span></div>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: 'rgba(255,255,255,0.4)' }} />
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full h-10 text-[13px] rounded-md outline-none focus:ring-0 pl-9 pr-3 placeholder:text-white/30"
          style={inputStyle}
        />
      </div>
    </div>
  )
}
